/**
 * Presentational mappings shared across the pickup pages: eligibility verdict -> pill class + label,
 * pickup status -> pill class + label. Centralized so Member Lookup, Eligible Members, and the
 * Pickups queue stay visually consistent with the ClariCommand pill vocabulary.
 */
import type { EligibilityBucket, PickupStatus } from '@/lib/pickups-types';

export interface PillSpec {
  /** The .pill modifier class (or '' for the muted default). */
  cls: string;
  label: string;
}

const VERDICT_PILLS: Record<EligibilityBucket, PillSpec> = {
  eligible: { cls: 'pill-success', label: 'Eligible' },
  bad_address: { cls: 'pill-warning', label: 'Bad address' },
  ineligible_no_svc: { cls: 'pill-danger', label: 'No carrier service' },
  unknown: { cls: '', label: 'Unknown' },
};

export function verdictPill(bucket: EligibilityBucket): PillSpec {
  return VERDICT_PILLS[bucket] ?? VERDICT_PILLS.unknown;
}

const STATUS_PILLS: Record<PickupStatus, PillSpec> = {
  scheduled: { cls: 'pill-primary', label: 'Scheduled' },
  collected: { cls: 'pill-success', label: 'Collected' },
  canceled: { cls: '', label: 'Canceled' },
  failed: { cls: 'pill-danger', label: 'Failed' },
};

export function statusPill(status: PickupStatus): PillSpec {
  return STATUS_PILLS[status] ?? { cls: '', label: status };
}

/** Why a non-eligible member cannot be scheduled (inline reason under a disabled Send button). */
export function ineligibleReason(bucket: EligibilityBucket): string {
  switch (bucket) {
    case 'bad_address':
      return 'The member address could not be standardized by USPS, so a carrier pickup cannot be scheduled.';
    case 'ineligible_no_svc':
      return 'USPS reports no carrier service at this address (PO box or similar). Carrier pickup is not available.';
    case 'unknown':
      return 'Eligibility has not been determined for this member yet.';
    default:
      return '';
  }
}

/** Human label for the SMS block reasons returned by POST /api/pickups/sms. */
export function smsBlockedLabel(reason: 'do_not_solicit' | 'no_phone'): string {
  return reason === 'do_not_solicit'
    ? 'Blocked: the member is flagged do-not-solicit.'
    : 'Blocked: no phone number is on file for this member.';
}
